import React, { FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { isEqual } from 'lodash'
import { Typography, Box, MenuItem, Tooltip, Grid } from '@mui/material'
import { ThreeDotMenu } from '../../../components'
import { ContractAuthorizedPersonType } from '../../../graphql'
import { authorizedPersonStatus } from '../../../constants'
import { dateFormat, rightsTeamRole } from '../../../utils'
import { TeamTableRowProps } from '../../../types'
import { getFullName } from '../helpers'
import { useWatchDetails } from './hooks'
import { MailPhoneWrap, StyledMobLabel } from './StyledComponents'

const TeamTableListRowTabletComponent: FC<TeamTableRowProps> = ({
  isShouldDisabled,
  teammate,
  teamMenuOptions,
  isViewOnly,
  isMyself,
}) => {
  const { t } = useTranslation()

  const {
    id,
    status,
    isAuthorizedSignatory,
    limitedAccessRight,
    signatoryRight,
    groupSize,
    person,
    invitationDate,
  } = teammate as ContractAuthorizedPersonType

  const watchDetails = useWatchDetails(id)

  const menuOptions = [t('deactivate', 'Deactivate')]

  const handleMenu = useCallback(() => {
    if (!status || !(status in teamMenuOptions)) return
    const option = teamMenuOptions[status]

    if (option && id) {
      option.handler(id)
    }
  }, [status, teamMenuOptions, id])

  return (
    <Box mt={1} borderTop="1px solid rgba(0, 0, 0, 0.1)" onClick={watchDetails}>
      <Grid container py={1.25} borderBottom="1px solid rgba(0, 0, 0, 0.1)" spacing={1}>
        <Grid size={isViewOnly ? 6 : 5} display="flex" alignItems="center">
          <Grid>
            <StyledMobLabel>{t('name', 'Name')}</StyledMobLabel>
            <Typography sx={{ textDecoration: 'underline' }} noWrap>
              {getFullName(person)}
            </Typography>
          </Grid>
        </Grid>
        <Grid size={3}>
          <StyledMobLabel>{t('role', 'Role')}</StyledMobLabel>
          <Typography>
            {isAuthorizedSignatory
              ? rightsTeamRole(signatoryRight as string, groupSize ?? 0)
              : rightsTeamRole(limitedAccessRight as string)}
          </Typography>
        </Grid>
        <Grid size={3}>
          <StyledMobLabel>{t('email', 'Email')}</StyledMobLabel>
          <MailPhoneWrap>
            <a href={`mailto:${person?.email}`} onClick={(e) => e.stopPropagation()}>
              {person?.email}
            </a>
          </MailPhoneWrap>
        </Grid>
        {!isViewOnly && (
          <Grid size={1}>
            <ThreeDotMenu disabled={isShouldDisabled}>
              {menuOptions.map((option, index) =>
                isMyself && option === 'Deactivate' ? (
                  <Tooltip
                    key={index}
                    title={t('cantDeactYourslf', 'You cannot deactivate yourself')}
                  >
                    <Box>
                      <MenuItem key={index} onClick={handleMenu} disabled>
                        {t(option)}
                      </MenuItem>
                    </Box>
                  </Tooltip>
                ) : (
                  <MenuItem key={index} onClick={handleMenu}>
                    {t(option)}
                  </MenuItem>
                ),
              )}
            </ThreeDotMenu>
          </Grid>
        )}
      </Grid>
      <Grid container py={1.25} borderBottom="1px solid rgba(0, 0, 0, 0.1)">
        <Grid size={5}>
          <StyledMobLabel>{t('invited', 'Invited')}</StyledMobLabel>
          <Typography>
            {invitationDate ? dateFormat(invitationDate) : t('never', 'Never')}
          </Typography>
        </Grid>
        <Grid size={3}>
          <StyledMobLabel>{t('lastSeen', 'Last seen')}</StyledMobLabel>
          <Typography>
            {person?.lastSeenAt ? dateFormat(person?.lastSeenAt) : t('never', 'Never')}
          </Typography>
        </Grid>
        <Grid size={4} display="flex" alignItems="center">
          <Box
            component="span"
            className={`roundedPill ${authorizedPersonStatus[status as string]}`}
          >
            {t(authorizedPersonStatus[status as string])}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export const TeamTableListRowTablet = React.memo(TeamTableListRowTabletComponent, isEqual)
